import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router";
import { t } from "../i18n/t";
import { cx } from "./klasy";
import type { KomponentIkony } from "./ikony";
import "../styles/wspolne.css";

export type WariantPrzycisku = "zwykly" | "glowny" | "jasny" | "cichy" | "obrys" | "niebezpieczny" | "niebezpieczny-pelny" | "ok";
export type RozmiarPrzycisku = "maly" | "zwykly" | "duzy";

function klasaPrzycisku(wariant: WariantPrzycisku, rozmiar: RozmiarPrzycisku, pelny?: boolean, ladowanie?: boolean, className?: string) {
  return cx(
    "iot-btn",
    wariant !== "zwykly" && `iot-btn--${wariant}`,
    rozmiar !== "zwykly" && `iot-btn--${rozmiar}`,
    pelny && "iot-btn--pelny",
    ladowanie && "is-loading",
    className,
  );
}

export interface PrzyciskProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  wariant?: WariantPrzycisku;
  rozmiar?: RozmiarPrzycisku;
  /** Ikona przed tekstem */
  ikona?: KomponentIkony;
  /** Ikona po tekście (np. strzałka „dalej”) */
  ikonaPo?: KomponentIkony;
  /** Na całą szerokość rodzica */
  pelny?: boolean;
  /** Pokazuje kółko ładowania i blokuje przycisk */
  ladowanie?: boolean;
  /** "submit" tylko w formularzach - domyślnie "button" */
  typ?: "button" | "submit";
  children: ReactNode;
}

/** Przycisk tekstowy. Domyślnie type="button". */
export function Przycisk({ wariant = "zwykly", rozmiar = "zwykly", ikona: Ikona, ikonaPo: IkonaPo, pelny, ladowanie, typ = "button", className, disabled, children, ...reszta }: PrzyciskProps) {
  return (
    <button
      type={typ === "submit" ? "submit" : "button"}
      className={klasaPrzycisku(wariant, rozmiar, pelny, ladowanie, className)}
      disabled={disabled || ladowanie}
      aria-busy={ladowanie || undefined}
      {...reszta}
    >
      {ladowanie ? <span className="iot-wirujace" aria-hidden="true" /> : Ikona && <Ikona />}
      {children}
      {IkonaPo && <IkonaPo />}
    </button>
  );
}

export interface PrzyciskLinkProps {
  do: string;
  wariant?: WariantPrzycisku;
  rozmiar?: RozmiarPrzycisku;
  ikona?: KomponentIkony;
  ikonaPo?: KomponentIkony;
  pelny?: boolean;
  className?: string;
  children: ReactNode;
}

/** Link (react-router) wyglądający jak przycisk - do przejść między ekranami. */
export function PrzyciskLink({ do: cel, wariant = "zwykly", rozmiar = "zwykly", ikona: Ikona, ikonaPo: IkonaPo, pelny, className, children }: PrzyciskLinkProps) {
  return (
    <Link to={cel} className={klasaPrzycisku(wariant, rozmiar, pelny, false, className)}>
      {Ikona && <Ikona />}
      {children}
      {IkonaPo && <IkonaPo />}
    </Link>
  );
}

export interface PrzyciskIkonaProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "aria-label"> {
  ikona: KomponentIkony;
  /** Tekst dla czytników ekranu (wymagany - przycisk nie ma widocznego tekstu) */
  etykieta: string;
  obrys?: boolean;
  /** Licznik w rogu (np. nowe alarmy) */
  licznik?: number;
  /** Stan przełącznika (np. ulubione) - ustawia aria-pressed */
  wcisniety?: boolean;
  obrocona?: boolean;
}

/** Okrągły przycisk z samą ikoną, 48 px. */
export function PrzyciskIkona({ ikona: Ikona, etykieta, obrys, licznik, wcisniety, obrocona, className, ...reszta }: PrzyciskIkonaProps) {
  return (
    <button
      type="button"
      className={cx("iot-btn-ikona", obrys && "iot-btn-ikona--obrys", className)}
      aria-label={etykieta}
      aria-pressed={wcisniety}
      {...reszta}
    >
      <Ikona obrocona={obrocona} />
      {licznik !== undefined && licznik > 0 && (
        <span className="iot-licznik" aria-hidden="true">{licznik > 99 ? "99+" : licznik}</span>
      )}
    </button>
  );
}

export interface PrzyciskIkonaLinkProps {
  do: string;
  ikona: KomponentIkony;
  etykieta: string;
  licznik?: number;
  obrys?: boolean;
}

/** Ikona-link (np. dzwonek alarmów w pasku). */
export function PrzyciskIkonaLink({ do: cel, ikona: Ikona, etykieta, licznik, obrys }: PrzyciskIkonaLinkProps) {
  const opis = licznik ? t("{etykieta} - nowe: {liczba}", { etykieta, liczba: licznik }) : etykieta;
  return (
    <Link to={cel} className={cx("iot-btn-ikona", obrys && "iot-btn-ikona--obrys")} aria-label={opis}>
      <Ikona />
      {licznik !== undefined && licznik > 0 && <span className="iot-licznik" aria-hidden="true">{licznik > 99 ? "99+" : licznik}</span>}
    </Link>
  );
}

export interface GrupaPrzyciskowProps {
  children: ReactNode;
  className?: string;
}

export function GrupaPrzyciskow({ children, className }: GrupaPrzyciskowProps) {
  return <div className={cx("iot-grupa-btn", className)}>{children}</div>;
}
